import React, { useEffect, useState } from "react";
import moment from "moment";
import AsyncSelect from "react-select/async";
import {
    Button, Card, CardHeader, CardBody, CardTitle,
    Table, Container, Row, Col, Spinner, Badge,
    Modal, ModalHeader, ModalBody, ModalFooter,
    Form, FormGroup, Label, Input
} from "reactstrap";
import membershipRemindersService from "../../services/api/membership-reminders-service";
import projectService from "../../services/api/projects-service";

const RECORDS_PER_PAGE = 20;

const emptyCampaignForm = {
    name: "",
    start_date: "",
    end_date: "",
    message_body: "",
    pre_expiry_message: "",
    post_expiry_message: "",
};

const loadProjectOptions = async (inputValue) => {
    const filters = { projectname: inputValue || "", status: 1 };
    const projectsData = await projectService.getAllProjects(filters, 1, 30);
    if (!projectsData.status || !Array.isArray(projectsData.data)) return [];
    return projectsData.data.map((p) => ({
        value: p.id || p._id,
        label: `${p.name || "(no name)"} — ${p.slug || ""}`,
    }));
};

const CampaignManager = () => {
    const [campaigns, setCampaigns] = useState([]);
    const [count, setCount] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyCampaignForm);
    const [formProject, setFormProject] = useState(null);
    const [filterProject, setFilterProject] = useState(null);
    const [error, setError] = useState("");

    const fetchCampaigns = async (projectId, pageNum = 1) => {
        setLoading(true);
        try {
            const result = await membershipRemindersService.getCampaigns(projectId || null, pageNum, RECORDS_PER_PAGE);
            if (result && result.success) {
                setCampaigns(result.campaigns);
                setCount(result.count);
            }
        } catch (err) {
            console.error("Error fetching campaigns:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCampaigns(filterProject ? filterProject.value : null, page);
    }, [filterProject, page]);

    const handleOpenCreate = () => {
        setEditingId(null);
        setForm(emptyCampaignForm);
        setFormProject(null);
        setError("");
        setModalOpen(true);
    };

    const handleOpenEdit = (campaign) => {
        setEditingId(campaign._id);
        setForm({
            name: campaign.name || "",
            start_date: campaign.start_date ? moment(campaign.start_date).format("YYYY-MM-DD") : "",
            end_date: campaign.end_date ? moment(campaign.end_date).format("YYYY-MM-DD") : "",
            message_body: campaign.message_body || "",
            pre_expiry_message: campaign.pre_expiry_message || "",
            post_expiry_message: campaign.post_expiry_message || "",
        });
        setFormProject(
            campaign.project_id
                ? { value: campaign.project_id._id || campaign.project_id, label: campaign.project_id.name || campaign.project_id }
                : null
        );
        setError("");
        setModalOpen(true);
    };

    const handleSave = async () => {
        if (!formProject) {
            setError("Please select a project.");
            return;
        }
        if (!form.name || !form.start_date || !form.end_date) {
            setError("Name, Start Date, and End Date are required.");
            return;
        }
        if (moment(form.start_date).isAfter(moment(form.end_date))) {
            setError("Start date cannot be after end date.");
            return;
        }
        setSaving(true);
        setError("");
        try {
            const payload = { ...form, project_id: formProject.value };
            let result;
            if (editingId) {
                result = await membershipRemindersService.updateCampaign({ ...payload, campaign_id: editingId });
            } else {
                result = await membershipRemindersService.createCampaign(payload);
            }
            if (result && result.success) {
                setModalOpen(false);
                fetchCampaigns(filterProject ? filterProject.value : null, page);
            } else {
                setError(result.message || "Failed to save campaign.");
            }
        } catch (err) {
            console.error("Error saving campaign:", err);
            setError("An unexpected error occurred.");
        } finally {
            setSaving(false);
        }
    };

    const handleToggle = async (campaignId) => {
        try {
            await membershipRemindersService.toggleCampaign(campaignId);
            fetchCampaigns(filterProject ? filterProject.value : null, page);
        } catch (err) {
            console.error("Error toggling campaign:", err);
        }
    };

    return (
        <Container fluid>
            <Row>
                <Col>
                    <Card>
                        <CardHeader>
                            <CardTitle tag="h4">Promotional Campaigns</CardTitle>
                            <p className="card-category text-muted">
                                Configure demand-based campaigns to re-engage inactive subscribers
                            </p>
                        </CardHeader>
                        <CardBody>
                            <Row className="mb-3 align-items-end">
                                <Col md={5}>
                                    <label style={{ fontWeight: 600, marginBottom: 4 }}>Filter by Project</label>
                                    <AsyncSelect
                                        isClearable
                                        placeholder="Search project..."
                                        loadOptions={loadProjectOptions}
                                        defaultOptions
                                        value={filterProject}
                                        onChange={(val) => { setFilterProject(val); setPage(1); }}
                                    />
                                </Col>
                                <Col md={7} className="text-right">
                                    <Button color="primary" onClick={handleOpenCreate}>
                                        + New Campaign
                                    </Button>
                                </Col>
                            </Row>

                            {loading ? (
                                <div className="text-center p-4"><Spinner color="primary" /></div>
                            ) : campaigns.length === 0 ? (
                                <p className="text-center text-muted p-4">No campaigns found. Create your first campaign.</p>
                            ) : (
                                <Table responsive striped>
                                    <thead className="text-primary">
                                        <tr>
                                            <th>#</th>
                                            <th>Campaign Name</th>
                                            <th>Project</th>
                                            <th>Start Date</th>
                                            <th>End Date</th>
                                            <th>Status</th>
                                            <th>Created</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {campaigns.map((c, idx) => (
                                            <tr key={c._id}>
                                                <td>{(page - 1) * RECORDS_PER_PAGE + idx + 1}</td>
                                                <td>{c.name}</td>
                                                <td>{c.project_id?.name || c.project_id || '-'}</td>
                                                <td>{c.start_date ? moment(c.start_date).format("DD MMM YYYY") : '-'}</td>
                                                <td>{c.end_date ? moment(c.end_date).format("DD MMM YYYY") : '-'}</td>
                                                <td>
                                                    <Badge color={c.is_active ? "success" : "secondary"}>
                                                        {c.is_active ? "Active" : "Inactive"}
                                                    </Badge>
                                                </td>
                                                <td>{c.createdAt ? moment(c.createdAt).format("DD MMM YYYY") : '-'}</td>
                                                <td>
                                                    <Button
                                                        size="sm"
                                                        color="info"
                                                        className="mr-2"
                                                        onClick={() => handleOpenEdit(c)}
                                                    >
                                                        Edit
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        color={c.is_active ? "warning" : "success"}
                                                        onClick={() => handleToggle(c._id)}
                                                    >
                                                        {c.is_active ? "Deactivate" : "Activate"}
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} size="lg">
                <ModalHeader toggle={() => setModalOpen(false)}>
                    {editingId ? "Edit Campaign" : "New Campaign"}
                </ModalHeader>
                <ModalBody>
                    {error && <div className="alert alert-danger">{error}</div>}
                    <Form>
                        <FormGroup>
                            <Label>Project *</Label>
                            <AsyncSelect
                                isClearable
                                placeholder="Search and select project..."
                                loadOptions={loadProjectOptions}
                                defaultOptions
                                value={formProject}
                                onChange={setFormProject}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>Campaign Name *</Label>
                            <Input
                                type="text"
                                value={form.name}
                                onChange={e => setForm({ ...form, name: e.target.value })}
                                placeholder="e.g. Diwali Sale 2025"
                            />
                        </FormGroup>
                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Start Date *</Label>
                                    <Input
                                        type="date"
                                        value={form.start_date}
                                        onChange={e => setForm({ ...form, start_date: e.target.value })}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>End Date *</Label>
                                    <Input
                                        type="date"
                                        value={form.end_date}
                                        onChange={e => setForm({ ...form, end_date: e.target.value })}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                        <FormGroup>
                            <Label>Campaign Message Body</Label>
                            <Input
                                type="textarea"
                                rows={4}
                                value={form.message_body}
                                onChange={e => setForm({ ...form, message_body: e.target.value })}
                                placeholder="Push notification & email body. Supports {renewal_link}, {days_to_expiry}."
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>Pre-Expiry Override Message (T-7 days)</Label>
                            <Input
                                type="textarea"
                                rows={3}
                                value={form.pre_expiry_message}
                                onChange={e => setForm({ ...form, pre_expiry_message: e.target.value })}
                                placeholder="Custom message for users expiring within 7 days. Leave blank to use default."
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>Post-Expiry Override Message (T+7 days)</Label>
                            <Input
                                type="textarea"
                                rows={3}
                                value={form.post_expiry_message}
                                onChange={e => setForm({ ...form, post_expiry_message: e.target.value })}
                                placeholder="Custom message for recently expired users. Leave blank to use default."
                            />
                        </FormGroup>
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setModalOpen(false)}>Cancel</Button>
                    <Button color="primary" onClick={handleSave} disabled={saving}>
                        {saving ? <Spinner size="sm" /> : (editingId ? "Update" : "Create")}
                    </Button>
                </ModalFooter>
            </Modal>
        </Container>
    );
};

export default CampaignManager;
